// @ts-strict-ignore
import type {Section} from 'common/components/items-menu/types';
import {translatable} from 'common/i18n/scope';

import {DateRange} from './date-range';
import {convertBaseDateToCalendarDate, isRelativeDate} from './utils';
import {DateRangeType, RelativeDate, TimeUnit} from './types';
import {DATE_RANGE_TYPE_DISPLAY_NAMES, TIME_COMPARISON_OPTION_LABELS, TIME_COMPARISON_OPTIONS} from './presets';
import translationKeys from './index.i18n';

/*
 * This class is used to support the Time Comparison Picker.
 */
export class RelativeDateRange extends DateRange {
  public getDefaultWindow(): RelativeDate {
    return {
      unit: TimeUnit.Day,
      value: 7,
    };
  }

  public getDefaultType(): DateRangeType {
    return DateRangeType.StartsOn;
  }

  public getDateRangeTypeOptions(): DateRangeType[] {
    return [DateRangeType.StartsOn, DateRangeType.EndsOn, DateRangeType.RelativeAfter];
  }

  public getTimeUnitOptions(): TimeUnit[] {
    return [TimeUnit.Day, TimeUnit.Week, TimeUnit.Month, TimeUnit.Quarter, TimeUnit.Year];
  }

  // Time comparison picker only wants to show Starts On/Ends On but still has RelativeAfter type
  public getDateRangeTypeDisplaySections(selectedType): Section[] {
    const items = [DateRangeType.StartsOn, DateRangeType.EndsOn].map((type) => ({
      value: DATE_RANGE_TYPE_DISPLAY_NAMES[type],
      label: DATE_RANGE_TYPE_DISPLAY_NAMES[type],
      isSelected: type === selectedType,
    }));
    return [{items}];
  }

  public canUseRolling(): boolean {
    return true;
  }

  public isRolling(): boolean {
    return this.from && isRelativeDate(this.from);
  }

  public getCalendarFromDate(): string {
    return convertBaseDateToCalendarDate(this.from);
  }

  // These aren't normal DateRange presets, they should NOT be defined in the normal `DateRange.presets` or `DateRange.getPresetOptions()` methods.
  public static getTimeComparisonPresetOptions({withAlignment}: {withAlignment: boolean}): any {
    return [
      {
        label: TIME_COMPARISON_OPTION_LABELS[TimeUnit.Day],
        option: TIME_COMPARISON_OPTIONS.PREVIOUS_DAY,
        value: {
          type: DateRangeType.RelativeAfter,
          offset: {
            unit: TimeUnit.Day,
            value: 1,
          },
        },
      },
      ...(withAlignment
        ? [
            {
              label: TIME_COMPARISON_OPTION_LABELS[TimeUnit.Week],
              option: TIME_COMPARISON_OPTIONS.PREVIOUS_WEEK,
              value: {
                type: DateRangeType.RelativeAfter,
                offset: {
                  unit: TimeUnit.Week,
                  value: 1,
                },
              },
            },
            {
              label: TIME_COMPARISON_OPTION_LABELS[TimeUnit.Month],
              option: TIME_COMPARISON_OPTIONS.PREVIOUS_MONTH,
              value: {
                type: DateRangeType.RelativeAfter,
                offset: {
                  unit: TimeUnit.Month,
                  value: 1,
                },
              },
            },
            {
              label: TIME_COMPARISON_OPTION_LABELS[TimeUnit.Quarter],
              option: TIME_COMPARISON_OPTIONS.PREVIOUS_QUARTER,
              value: {
                type: DateRangeType.RelativeAfter,
                offset: {
                  unit: TimeUnit.Quarter,
                  value: 1,
                },
              },
            },
          ]
        : [
            {
              label: translatable(translationKeys, ({t}) =>
                t(`reports.lift-comparison.previous-period.with-value`, {
                  count: 7,
                  unit: t(`reports.lift-comparison.previous-period.units.day`),
                }),
              ),
              option: TIME_COMPARISON_OPTIONS.PREVIOUS_7_DAYS,
              value: {
                type: DateRangeType.RelativeAfter,
                offset: {
                  unit: TimeUnit.Day,
                  value: 7,
                },
              },
            },
            {
              label: translatable(translationKeys, ({t}) =>
                t(`reports.lift-comparison.previous-period.with-value`, {
                  count: 30,
                  unit: t(`reports.lift-comparison.previous-period.units.day`),
                }),
              ),
              option: TIME_COMPARISON_OPTIONS.PREVIOUS_30_DAYS,
              value: {
                type: DateRangeType.RelativeAfter,
                offset: {
                  unit: TimeUnit.Day,
                  value: 30,
                },
              },
            },
            {
              label: translatable(translationKeys, ({t}) =>
                t(`reports.lift-comparison.previous-period.with-value`, {
                  count: 3,
                  unit: t(`reports.lift-comparison.previous-period.units.month`),
                }),
              ),
              option: TIME_COMPARISON_OPTIONS.PREVIOUS_3_MONTHS,
              value: {
                type: DateRangeType.RelativeAfter,
                offset: {
                  unit: TimeUnit.Month,
                  value: 3,
                },
              },
            },
          ]),
      {
        label: TIME_COMPARISON_OPTION_LABELS[TimeUnit.Year],
        option: TIME_COMPARISON_OPTIONS.PREVIOUS_YEAR,
        value: {
          type: DateRangeType.RelativeAfter,
          offset: {
            unit: TimeUnit.Year,
            value: 1,
          },
        },
      },
    ];
  }
}
